import {
  createQColumnHelper,
  DataProvider,
  Filtering,
  Pagination,
  QBox,
  QButton,
  QButtonGroup,
  QDataTable,
  QDivider,
  QHeading,
  QSelectItem,
  QSpinner,
  QStack,
  useCurrentUser,
} from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import ChangeEventTemplateOwnerModal from '../../../components/ChangeEventTemplateOwnerModal/ChangeEventTemplateOwnerModal';
import CreateEditEventTemplateModal from '../../../components/CreateEditEventTemplateModal/CreateEditEventTemplateModal';
import DeleteDraftEventTemplateConfirmationModal from '../../../components/DeleteDraftEventTemplateConfirmationModal/DeleteDraftEventTemplateConfirmationModal';
import MakeEffectiveModal from '../../../components/MakeEffectiveModal/MakeEffectiveModal';
import * as DisplayStrings from '../../../displayStrings';
import { useDocTitle, useUsers } from '../../../hooks';
import { EventType, VersionedTemplateStatusValues } from '../../../types';
import TemplateRowActions from './TemplateRowActions';
import { useGetTemplates } from './hooks';
import { Link } from 'react-router-dom';
import { convertEpochToJsDate } from '../../../utils/datetimeUtils';
import { statusToDisplayString, templateStatus } from '../../../utils/eventTemplateUtils';
import { eventTemplateRowActions } from '../../../utils/eventTemplatePermissionUtils';
import { eventTemplatesTablePersistanceKey } from '../../../types/common/constants';
import ActiveStatusFilter from '../../../components/EventTable/components/ActiveStatusFilter';
import { StatusFilter } from './statusFilter';

const filterBySearchTerm = (item: EventType, searchTerm: string | undefined): boolean => {
  if (!searchTerm) {
    return true;
  }
  const term = searchTerm.toLowerCase();
  return item.name.toLowerCase().includes(term) || item.prefix.toLowerCase().includes(term);
};

type SelectedTemplate = {
  templateId: number;
  lineageId: string;
  ownerName?: string;
  ownerId?: string;
};

const QualityEventTemplates = () => {
  const { t } = useTranslation();
  const { companyId, userId, userGroups } = useCurrentUser();
  const isQualityUser = userGroups[0] === 'quality';

  const [isCreateEventModalOpen, setIsCreateEventModalOpen] = useState<boolean>(false);
  const [isMakeEffectiveModalOpen, setIsMakeEffectiveModalOpen] = useState<boolean>(false);
  const [isChangeOwnerModalOpen, setIsChangeOwnerModalOpen] = useState<boolean>(false);
  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate>();
  const [versionedStatusFilter, setVersionedStatusFilter] = useState<VersionedTemplateStatusValues>('allActive');
  const { data: templates, isLoading } = useGetTemplates(companyId, versionedStatusFilter);

  useDocTitle('Event templates - Qualio');

  const { data: usersData, isLoading: usersDataLoading } = useUsers();

  const setTemplateForMakeEffective = useCallback((templateId: number, lineageId: string) => {
    setSelectedTemplate({ templateId, lineageId });
    setIsMakeEffectiveModalOpen(true);
  }, []);

  const setTemplateForChangeOwner = useCallback(
    (templateId: number, lineageId: string, ownerName: string, ownerId: string) => {
      setSelectedTemplate({ templateId, lineageId, ownerName, ownerId });
      setIsChangeOwnerModalOpen(true);
    },
    [],
  );

  const setTemplateForDeleteDraft = useCallback((templateId: number, lineageId: string) => {
    setSelectedTemplate({ templateId, lineageId });
    setIsDeleteDraftModalOpen(true);
  }, []);

  const columnHelper = createQColumnHelper<EventType>();

  const columns = useMemo(() => {
    return [
      columnHelper.text('prefix', { header: t('common.labels.prefix'), width: '120px', id: 'prefix' }),
      {
        accessorKey: 'title',
        header: t('common.labels.title'),
        cell: ({ row: { original: template } }: { row: { original: EventType } }) => {
          return <Link to={`${template.id}`}>{template.name}</Link>;
        },
        meta: {
          minWidth: '470px',
        },
      },
      columnHelper.text((template) => `${template.major_version}.${template.minor_version}`, {
        header: t('common.labels.version'),
        width: '120px',
        id: 'version',
      }),
      columnHelper.text((template) => template.defaultOwner, {
        header: t('templates.labels.defaultAssignee'),
        width: '170px',
        id: 'defaultAssignee',
      }),
      columnHelper.text((template) => `${template.defaultTimeLimit} days`, {
        header: t('templates.labels.defaultTimeLimit'),
        width: '180px',
        id: 'defaultTimeLimit',
      }),
      columnHelper.date((template) => (template.updatedAt ? convertEpochToJsDate(template.updatedAt) : undefined), {
        header: t('common.labels.lastModified'),
        width: '150px',
        id: 'lastModified',
      }),
      columnHelper.status((template) => statusToDisplayString[templateStatus(template)], {
        header: t('common.labels.status'),
        id: 'status',
        statuses: {
          [DisplayStrings.TemplateDraft]: 'gray',
          [DisplayStrings.TemplateEffective]: 'green',
          [DisplayStrings.TemplateSuperseded]: 'red',
          [DisplayStrings.TemplateArchived]: 'red',
        },
        width: '120px',
      }),
      columnHelper.menu({
        hide: (template: EventType) =>
          template.status === 'superseded' ||
          !eventTemplateRowActions(template, userId, isQualityUser).hasVisibleActions,
        items: (
          <TemplateRowActions
            setTemplateForMakeEffective={setTemplateForMakeEffective}
            setTemplateForChangeOwner={setTemplateForChangeOwner}
            setTemplateForDeleteDraft={setTemplateForDeleteDraft}
            companyId={companyId}
          />
        ),
      }),
    ];
  }, [
    columnHelper,
    companyId,
    isQualityUser,
    setTemplateForChangeOwner,
    setTemplateForDeleteDraft,
    setTemplateForMakeEffective,
    t,
    userId,
  ]);

  const statusValueLabelMap: Record<string, string> = useMemo(
    () => ({
      allActive: DisplayStrings.TemplateAllActiveStatuses,
      draft: DisplayStrings.TemplateDraft,
      effective: DisplayStrings.TemplateEffective,
      superseded: DisplayStrings.TemplateSuperseded,
      archived: DisplayStrings.TemplateArchived,
    }),
    [],
  );

  const statusList = useMemo(
    () =>
      Object.keys(statusValueLabelMap).map((key) => ({ value: key, label: statusValueLabelMap[key] }) as QSelectItem),
    [statusValueLabelMap],
  );

  const statusMap = useMemo(() => {
    return statusList.reduce(
      (acc, status) => {
        acc[status['value']] = status['label'];
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [statusList]);

  const activeStatusFilterRender = useCallback(
    (value: unknown) => <ActiveStatusFilter value={value} statusMap={statusMap} />,
    [statusMap],
  );

  const filterDefinitions: Filtering.FilterDefinitions = useMemo(() => {
    return {
      status: {
        label: t('common.labels.status'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: activeStatusFilterRender,
        defaultValue: ['allActive'],
      },
    };
  }, [activeStatusFilterRender, t]);

  return (
    <>
      <QStack direction="column" spacing={6}>
        <QBox display="flex" flexDirection="row" justifyContent="space-between">
          <QHeading size="lg">{t('templates.labels.eventTemplates')}</QHeading>
          <QButtonGroup>
            <QButton data-cy={'create-template'} variant="solid" onClick={() => setIsCreateEventModalOpen(true)}>
              {t('templates.actions.createEventTemplate')}
            </QButton>
          </QButtonGroup>
        </QBox>
        <QDivider />
        {isLoading || usersDataLoading ? (
          <QBox w="100%" textAlign="center" p={5} mt={16}>
            <QSpinner />
          </QBox>
        ) : (
          <QBox data-cy={'event-templates-table'}>
            <Pagination.Auto itemCount={templates?.length} clientSide>
              <Filtering.FilterProvider
                searchTermKey="search"
                definitions={filterDefinitions}
                persistanceKey={eventTemplatesTablePersistanceKey}
              >
                <DataProvider.Fixed data={templates ?? []} filterOption={filterBySearchTerm}>
                  <QDataTable enableSorting columns={columns}>
                    <Filtering.CustomFormContent>
                      <QStack spacing={4}>
                        <StatusFilter filterOptions={statusList} setVersionedStatusFilter={setVersionedStatusFilter} />
                      </QStack>
                    </Filtering.CustomFormContent>
                  </QDataTable>
                </DataProvider.Fixed>
              </Filtering.FilterProvider>
            </Pagination.Auto>
          </QBox>
        )}
      </QStack>
      {usersData && (
        <CreateEditEventTemplateModal
          isOpen={isCreateEventModalOpen}
          setIsOpen={setIsCreateEventModalOpen}
          allUsers={usersData}
        />
      )}
      {selectedTemplate && (
        <MakeEffectiveModal
          isOpen={isMakeEffectiveModalOpen}
          setIsOpen={setIsMakeEffectiveModalOpen}
          templateId={selectedTemplate.templateId}
          lineageId={selectedTemplate.lineageId}
        />
      )}
      {selectedTemplate && (
        <>
          <DeleteDraftEventTemplateConfirmationModal
            isOpen={isDeleteDraftModalOpen}
            setIsOpen={setIsDeleteDraftModalOpen}
            lineageId={selectedTemplate.lineageId}
          />
          <ChangeEventTemplateOwnerModal
            isOpen={isChangeOwnerModalOpen}
            setIsOpen={setIsChangeOwnerModalOpen}
            templateId={selectedTemplate.templateId}
            lineageId={selectedTemplate.lineageId}
            currentOwnerName={selectedTemplate.ownerName!}
            currentOwnerId={String(selectedTemplate.ownerId!)}
          />
        </>
      )}
    </>
  );
};

export default QualityEventTemplates;
